import React, { FC, useState, useEffect, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import t from "../../localization/en/translation.json";
import {
  AppButton,
  AppModal,
  AppIcon,
  AppAlert,
  AppIconButton,
} from "../../theme";
import { exclamation, xMark } from "../../theme/icons";
import StepperIndicator from "../stepperIndicator/stepperIndicator";

import cloudUpload from "../../assets/images/icons/cloud-upload.svg";
import excelIcon from "../../assets/images/icons/microsoft-excel.svg";
import fileIcon from "../../assets/images/icons/fileSpreadsheet.svg";

import "./importUserModal.scss";

interface IProps {
  isOpen: boolean;
  bulkImportTemplatedownload: Function;
  bulkUserUploadRequest: Function;
  bulkUserUploadErrors: any;
  bulkImportSave: Function;
  bulkImportSaveData: boolean;
  closeImportModal: Function;
}

const ImportUserModal: FC<IProps> = ({
  isOpen,
  bulkImportTemplatedownload,
  bulkUserUploadRequest,
  bulkUserUploadErrors,
  bulkImportSave,
  bulkImportSaveData,
  closeImportModal,
}) => {
  const [bulkUserCurrentState, setBulkUserCurrentState] = useState(1);
  const [fileName, updateFileName] = useState("");

  const onDrop = useCallback((acceptedFiles) => {
    const formData = new FormData();
    acceptedFiles.forEach((acceptedFile) => {
      updateFileName(acceptedFile.name);
      formData.append("file", acceptedFile);
    });

    if (acceptedFiles.length > 0) {
      bulkUserUploadRequest(formData);
      setBulkUserCurrentState(2);
    }
    // eslint-disable-next-line
  }, []);

  const onDragOver = (e) => {
    const event = e as Event;
    event.stopPropagation();
    event.preventDefault();
  };

  const onDragEnter = (e) => {
    const event = e as Event;
    event.stopPropagation();
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    accept: ".xlsm",
    maxFiles: 1,
    maxSize: 73400320,
    onDrop,
    onDragOver,
    onDragEnter,
    noDragEventsBubbling: true,
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <span>{t.admin_apps_rejected_apps_msg}</span>
  ));

  useEffect(() => {
    if (bulkImportSaveData) {
      setBulkUserCurrentState(3);
    }
  }, [bulkImportSaveData]);

  const onConfirm = () => {
    bulkImportSave();
  };

  const onClose = () => {
    setBulkUserCurrentState(1);
    closeImportModal();
  };

  const downloadTemplate = () => {
    bulkImportTemplatedownload();
  };

  const removeFile = () => {
    setBulkUserCurrentState(1);
  };

  const reUploadFile = () => {
    setBulkUserCurrentState(1);
  };

  const stepData = [
    { step_text: "", title_text: t.manage_user_bulk_users_upload },
    { step_text: "", title_text: t.manage_user_bulk_users_validate },
    { step_text: "", title_text: t.manage_user_toolbar_import },
  ];
  return (
    <AppModal
      modalClass="manage-user-add-user"
      showModal={isOpen}
      additionalBtns={
        <>
          {(bulkUserCurrentState === 2 && bulkUserUploadErrors && !bulkUserUploadErrors?.data?.status && (
            <AppButton type="button" className="filename-action reupload" onClick={reUploadFile}>
              {t.reupload}
            </AppButton>
          ))}
          {(bulkUserCurrentState === 3 && (
            <AppButton type="button" className="filename-action close" onClick={onClose}>
              {t.Close}
            </AppButton>
          ))}
        </>
      }
      onModalClose={() => onClose()}
      onConfirm={() => onConfirm()}
      cancelBtnText={t.cancel}
      confirmBtnText={t.manage_user_toolbar_import}
      title={t.manage_user_toolbar_import}
      disabledValue={!bulkUserUploadErrors || !bulkUserUploadErrors?.data?.status}
      hideConfirmButton={(!bulkUserUploadErrors ||
        !bulkUserUploadErrors?.data?.status ||
        bulkUserCurrentState === 1 ||
        bulkUserCurrentState === 3) || false}
      hideFooterCancelBtn={bulkUserCurrentState === 3}
    >
      <>
        <div className="stepper-container">
          <StepperIndicator
            stepIndicatorState={{ currentStep: bulkUserCurrentState }}
            stepData={stepData}
            handleStepClick={() => { }}
          />
        </div>
        {bulkUserCurrentState === 1 && (
          <>
            <div className="button-wrapper">
              <div className="upload-file-text">{t.manage_user_bulk_users_upload_file}</div>
              <div className="download-template-btn" role="button" onClick={downloadTemplate} title={t.manage_user_bulk_users_download}>
                <img src={excelIcon} className="excel-icon" alt="download-template-icon" />
                {t.manage_user_bulk_users_download}
              </div>
            </div>
            <div
              className={
                fileRejectionItems.length > 0
                  ? "drag-drop-wrapper-alert"
                  : "drag-drop-wrapper"
              }
            >
              <div {...getRootProps({ className: "dropzone" })}>
                <input {...getInputProps()} />
                {fileRejectionItems.length > 0 ? (
                  <>
                    <AppIcon
                      className="upload-icon-size"
                      icon={exclamation}
                    />
                    <span className="upload-options">
                      {t.try_again} &nbsp;<span className="upload-text">{t.click_to_upload}</span>
                    </span>
                    <span className="upload-options">{fileRejectionItems}</span>
                  </>
                ) : (
                  <>
                    <img src={cloudUpload} className="upload-icon-size" alt="Upload" />
                    <span>
                      <span className="upload-text">{t.click_to_upload}</span>&nbsp; {t.drag_to_upload}
                    </span>
                    <div>xlsm</div>
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {bulkUserCurrentState === 2 && (
          <>
            <div className="filename-container">
              <div className="filename">
                <img src={fileIcon} className="file-icon" alt="file-icon" />
                <div>
                  {fileName}
                  {(bulkUserUploadErrors && bulkUserUploadErrors?.data?.status && (
                    <div className="no-errors">{t.bulk_no_errors}</div>
                  ))}
                </div>
              </div>
              <AppIconButton
                aria-label="close"
                className="modal-close-icon"
                onClick={removeFile}
                title="Remove"
              >
                <AppIcon icon={xMark} />
              </AppIconButton>
            </div>
            <div className="upload-validation">
              {(bulkUserUploadErrors &&
                !bulkUserUploadErrors?.data?.status &&
                !bulkUserUploadErrors?.error && (
                  <AppAlert severity={'error'} className="import-users-alerts">
                    {t.bulk_error}
                  </AppAlert>
                ))}
              {bulkUserUploadErrors?.error && (
                <AppAlert severity={'error'} className="import-users-alerts">
                  {t.bulk_error_something_wrong}
                </AppAlert>
              )}
            </div>
            {(bulkUserUploadErrors && bulkUserUploadErrors?.data?.errors?.length && (
              <div className="validation-errors">
                <div className="validation-header">
                  {bulkUserUploadErrors?.data?.errors.some(err => !err.isWarning) ? t.bulk_errors_alert : t.bulk_alert}
                  <div className="alerts-count">{bulkUserUploadErrors?.data?.errors.length}</div>
                </div>
                <div className="validation-row row-header">
                  <div className="validation-field hdr key-cell">
                    {t.bulk_cell}
                  </div>
                  <div className="validation-field hdr key-field">
                    {t.bulk_field}
                  </div>
                  <div className="validation-field hdr key-error">
                    {t.bulk_filed_error}
                  </div>
                </div>
                {bulkUserUploadErrors?.data?.errors.map((error, index) => (
                  <div
                    className="validation-row row-content"
                    key={`${index}_${error.cell}`}
                  >
                    <div className="validation-field key-cell">
                      {error.cell || "-"}
                    </div>
                    <div className="validation-field key-field">
                      {error.fieldAffected || "-"}
                    </div>
                    <div className={`validation-field key-error ${!error.isWarning ? 'error' : ''}`}>
                      {error.error || "-"}
                    </div>
                  </div>
                ))}
              </div>
            )) ||
              null}
          </>
        )}
        {bulkUserCurrentState === 3 && (
          <>
            <div className="bulk-import-results">
              {bulkUserUploadErrors?.success ? (
                <AppAlert severity={'success'} className="import-users-alerts">
                  {t.manage_user_bulk_users_file_imported}
                </AppAlert>
              ) :
                (
                  <AppAlert severity={'error'} className="import-users-alerts">
                    {t.manage_user_bulk_users_file_imported_error}
                  </AppAlert>
                )}
            </div>
          </>
        )}
      </>
    </AppModal>
  );
};

export default ImportUserModal;
